import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCustomers } from "../../../../../api/customers";
import { getDisplayAccountType } from "../../../../../utils/accountTypeUtils";
import { buildPlatformCustomerScopeParams } from "../../utils/platformScope";

export default function useAccountsTabData({ profile, rowsPerPage, sortConfig }) {
  const [realData, setRealData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [activeManagerFilter, setActiveManagerFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);

  const fetchRealDataRef = useRef();
  const latestRequestIdRef = useRef(0);
  const transformCustomerToAccount = useCallback((customer) => {
    const getStatus = (entry) => {
      return entry.onboarding_stage === "completed" ? "Ready" : "Onboarding";
    };

    const getBookkeeper = (entry) => {
      return entry.bookkeeper || "";
    };

    return {
      id: customer.id,
      accountName: customer.name || "",
      primaryContact: customer.primary_contact || "",
      primaryEmail: customer.email || "",
      accountType: getDisplayAccountType(customer),
      status: getStatus(customer),
      manager: customer.manager || "",
      bookkeeper: getBookkeeper(customer),
      altUser: customer.alt_users_count || 0,
      altUserNames: null,
      originalCustomer: customer,
      customerId: customer.id,
    };
  }, []);

  const fetchRealData = useCallback(
    async (cursor = null, search = "", isNewSearch = false, filters = {}) => {
      if (!profile) {
        setRealData([]);
        setNextCursor(null);
        setPrevCursor(null);
        setLoading(true);
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setLoading(true);
      setError(null);

      try {
        const params = {
          page_size: rowsPerPage,
          include_profiles: false,
          include_quickbooks_connection: true,
        };

        if (cursor) {
          params.cursor = cursor;
        }

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (filters.accountType && filters.accountType.trim()) {
          params.account_types = filters.accountType.trim();
        }

        if (filters.accountName && filters.accountName.trim()) {
          params.name = filters.accountName.trim();
        }
        if (filters.primaryContact && filters.primaryContact.trim()) {
          params.primary_contact = filters.primaryContact.trim();
        }
        if (filters.primaryEmail && filters.primaryEmail.trim()) {
          params.email = filters.primaryEmail.trim();
        }
        if (filters.manager && filters.manager.trim()) {
          params.manager = filters.manager.trim();
        }
        if (filters.bookkeeper && filters.bookkeeper.trim()) {
          params.bookkeeper = filters.bookkeeper.trim();
        }

        if (filters.quickbooksStatus && filters.quickbooksStatus.trim()) {
          params.quickbooks_status = filters.quickbooksStatus.trim();
        }

        if (sortConfig && sortConfig.field && sortConfig.direction) {
          const fieldMap = {
            accountName: "name",
            primaryContact: "primary_contact",
            primaryEmail: "email",
            accountType: "account_type",
            manager: "manager",
            bookkeeper: "bookkeeper",
          };

          const backendField = fieldMap[sortConfig.field] || sortConfig.field;
          const prefix = sortConfig.direction === "desc" ? "-" : "";
          params.ordering = `${prefix}${backendField}`;
        }

        const response = await fetchCustomers(
          profile,
          buildPlatformCustomerScopeParams(profile, params)
        );
        const transformedData = (response.results || []).map(transformCustomerToAccount);

        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        setRealData(transformedData);
        setNextCursor(response.next ? new URL(response.next).searchParams.get("cursor") : null);
        setPrevCursor(response.previous ? new URL(response.previous).searchParams.get("cursor") : null);
      } catch (err) {
        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        setError("Failed to load accounts data");
      } finally {
        if (latestRequestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [profile, rowsPerPage, sortConfig, transformCustomerToAccount]
  );

  fetchRealDataRef.current = fetchRealData;

  const handleNextPage = useCallback(() => {
    if (nextCursor) {
      setPage((prev) => prev + 1);
      fetchRealDataRef.current(nextCursor, searchTerm, false, activeFilters);
    }
  }, [nextCursor, searchTerm, activeFilters]);

  const handlePreviousPage = useCallback(() => {
    if (prevCursor) {
      setPage((prev) => prev - 1);
      fetchRealDataRef.current(prevCursor, searchTerm, false, activeFilters);
    } else {
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, false, activeFilters);
    }
  }, [prevCursor, searchTerm, activeFilters]);

  const handleSearchChange = useCallback(
    (searchValue) => {
      if (searchValue === searchTerm) {
        return;
      }

      setSearchTerm(searchValue);
      setPage(0);
      fetchRealDataRef.current(null, searchValue, true, activeFilters);
    },
    [searchTerm, activeFilters]
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      setActiveFilters(newFilters);

      if (newFilters.manager && newFilters.manager.trim()) {
        const managerNames = newFilters.manager
          .split(",")
          .map((name) => name.trim())
          .filter((name) => name);
        setActiveManagerFilter(managerNames.length > 0 ? managerNames : null);
      } else {
        setActiveManagerFilter(null);
      }

      setPage(0);
      fetchRealDataRef.current(null, searchTerm, true, newFilters);
    },
    [searchTerm]
  );

  const handleManagerFilterChange = useCallback(
    (managers) => {
      if (!managers || managers.length === 0) {
        const newFilters = { ...activeFilters };
        delete newFilters.manager;
        setActiveFilters(newFilters);
        setActiveManagerFilter(null);
        setPage(0);
        fetchRealDataRef.current(null, searchTerm, true, newFilters);
      } else {
        const newFilters = { ...activeFilters, manager: managers.join(",") };
        setActiveFilters(newFilters);
        setActiveManagerFilter(managers);
        setPage(0);
        fetchRealDataRef.current(null, searchTerm, true, newFilters);
      }
    },
    [searchTerm, activeFilters]
  );

  const refresh = useCallback(() => {
    fetchRealDataRef.current(null, searchTerm, true, activeFilters);
  }, [searchTerm, activeFilters]);

  useEffect(() => {
    if (!profile) {
      latestRequestIdRef.current += 1;
      setRealData([]);
      setNextCursor(null);
      setPrevCursor(null);
      setLoading(true);
      return;
    }

    fetchRealDataRef.current(null, "", true, {});
  }, [profile]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    setPage(0);
    fetchRealDataRef.current(null, searchTerm, true, activeFilters);
  }, [profile, rowsPerPage, sortConfig, searchTerm, activeFilters]);

  return {
    data: realData,
    loading,
    error,
    searchTerm,
    page,
    nextCursor,
    prevCursor,
    activeManagerFilter,
    handleNextPage,
    handlePreviousPage,
    handleSearchChange,
    handleFiltersChange,
    handleManagerFilterChange,
    refresh,
  };
}
